import { Fragment, useRef, useState } from 'react';
import { Box, Chip } from '@mui/material';
import { getEmojiForReaction } from '../utils/getEmojiForReaction';
import { Reaction, ReactionType } from '../../../../generated/graphql';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFaceSmilePlus } from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import { ColorSets } from '@client/new/community/utils/ReactionChipColorVariants.map';
import {
  AddReactionPopover,
  AddReactionPopoverHandles,
} from './AddReactionPopover';
import { useReactionsBar } from '../hooks/useReactionsBar';

export interface PostReactionsColorSet {
  activeChipBorderColor: string;
  addEmojiChipColor: string;
  chipBackground: string;
  chipFontColor: string;
}

type Props = {
  reactions: Reaction[];
  colorSet?: PostReactionsColorSet;
  onToggleReaction?: (reactionType: ReactionType) => void;
};

export const PostReactions = ({
  reactions,
  colorSet: {
    activeChipBorderColor,
    addEmojiChipColor,
    chipBackground,
    chipFontColor,
  } = ColorSets.Default,
  onToggleReaction,
}: Props) => {
  const reactionPopover = useRef<AddReactionPopoverHandles>();
  const {
    visibleReactions,
    restReactionsCount,
    allReactionsVisible,
    toggleAllReactionsVisible,
  } = useReactionsBar({ maxIcons: 7, allReactions: reactions });

  const [emojisLoader, setEmojisLoader] = useState(new Set());

  const handleReactionPress = (type: ReactionType) => async () => {
    if (emojisLoader.has(type)) {
      return;
    }
    setEmojisLoader(emojisLoader.add(type));
    await onToggleReaction?.(type);
    if (emojisLoader.has(type)) {
      emojisLoader.delete(type);
      setEmojisLoader(emojisLoader);
    }
  };

  const handlePressLess: React.MouseEventHandler<HTMLDivElement> | undefined = (
    event,
  ) => {
    event.stopPropagation();
    toggleAllReactionsVisible();
  };

  const handlePressMore: React.MouseEventHandler<HTMLDivElement> | undefined = (
    event,
  ) => {
    event.stopPropagation();
    toggleAllReactionsVisible();
  };

  return (
    <Box className="flex flex-row flex-wrap gap-1.5 mt-2">
      {visibleReactions.map(({ type, count, isToggled }) => (
        <Chip
          disabled={emojisLoader.has(type)}
          className={classNames([
            chipFontColor,
            chipBackground,
            {
              [activeChipBorderColor]: isToggled,
            },
          ])}
          label={`${getEmojiForReaction(type)} ${count}`}
          key={type}
          onClick={(event) => {
            event.stopPropagation();
            /** TODO: https://app.shortcut.com/playbook/story/75454/toggling-reactions-admin-panel */
            handleReactionPress(type)();
          }}
        />
      ))}
      {restReactionsCount !== 0 ? (
        <Fragment>
          {allReactionsVisible ? (
            <Chip
              className={`bg-primary-150`}
              onClick={handlePressLess}
              label={'Show Less'}
            />
          ) : (
            <Chip
              className={`bg-primary-150`}
              onClick={handlePressMore}
              label={`${restReactionsCount} more`}
            />
          )}
        </Fragment>
      ) : null}
      <Chip
        className={classNames([addEmojiChipColor, chipBackground])}
        label={<FontAwesomeIcon icon={faFaceSmilePlus} />}
        onClick={(event) => {
          /** TODO: https://app.shortcut.com/playbook/story/75454/toggling-reactions-admin-panel */
          reactionPopover.current?.openMenu(event);
        }}
      />
      <AddReactionPopover
        ref={reactionPopover}
        onReactionPress={(type) => {
          handleReactionPress(type)();
        }}
      />
    </Box>
  );
};
